import React from 'react';
import moment from 'moment';
import Users from './Users';
import Companies from './Companies';
import { ProfileContext } from './dataProvider';
import routes from '~/apiRouter';
import messages from '~h/messages';
import { unescapeQuotesInObject } from '~h/text';
import { passportFormatting } from '../../../helpers/textFormatting';
import openErrorMsgForm from '~c/popups/ErrorSender';

class ProfilesTab extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      users: this.getUsersData(),
      companies: this.getCompaniesData(),
    };
  }

  mapRawUserData = user => {
    const processPassport = (passport, citizenshipCode) => {
      if (passport) {
        if (citizenshipCode === 'RU') {
          return passportFormatting(passport);
        }
        return passport;
      }
      return '';
    };

    return {
      id: user?.CONTACT_ID ?? '',
      email: user?.EMAIL ?? '',
      name: user?.FIRST_NAME ?? '',
      patronymic: user?.MIDDLE_NAME ?? '',
      lastName: user?.LAST_NAME ?? '',
      phone: user?.TELEPHONE ?? '',
      jobName: user?.JOB_TITLE ?? '',
      company: user?.COMPANY_NAME ?? '',
      companyPosition: user?.JOB_TITLE ?? '',
      passport: processPassport(user?.PASSPORT_NUMBER, user?.CITIZENSHIP_CODE),
      passportCreated: user?.PASSPORT_ISSUE_DATE
        ? moment(user?.PASSPORT_ISSUE_DATE, 'DD.MM.YYYY').format('DD.MM.YYYY')
        : '',
      birthday: user?.BIRTH_DATE
        ? moment(user?.BIRTH_DATE, 'DD.MM.YYYY').format('DD.MM.YYYY')
        : '',
      subscribe: user?.MKT_SUBSCRIPTION ?? false,
      passportPlaceCreated: user?.PASSPORT_AUTHORITY ?? '',
      addressOfRegistration: user?.REGISTRATION_ADDRESS ?? '',

      snils: user?.SNILS ?? '',
      diplomaNumber: user?.DIPLOMA_NUMBER ?? '',
      diplomaQualification: user?.DIPLOMA_QUALIFICATION ?? '',
      diplomaRegNumber: user?.DIPLOMA_REG_NUMBER ?? '',
      diplomaSeries: user?.DIPLOMA_SERIES ?? '',
      citizenship: user?.CITIZENSHIP || 'Российская Федерация',
      citizenshipCode: user?.CITIZENSHIP_CODE || 'RU',
    };
  };

  getUsersData = () => {
    const self = this.mapRawUserData(window?.user);
    self.personalPhoto = window.user?.SITE?.PERSONAL_PHOTO;
    const contacts = window.user?.CONTACTS.map(contact =>
      this.mapRawUserData(contact),
    );

    return [self, ...contacts];
  };

  mapRawCompanyData = company => {
    return {
      companyName: company.COMPANY_NAME ?? '',
      id: company.COMPANY_ID ?? '',
      inn: company.INN ?? '',
      kpp: company.KPP ?? '',
      city: company.CITY ?? '',
      cityCode: company.CITY_CODE ?? '',
      paymentAccount: company.BANK_ACCOUNT ?? '',
      paymentCorAccount: company.CORR_ACCOUNT ?? '',
      bankBik: company.BIK ?? '',
      bankName: company.BANK ?? '',
      address: company.LEGAL_ADDRESS ?? '',
      factAddress: company.LOCATION_ADDRESS ?? '',
    };
  };

  getCompaniesData = () =>
    window.user?.COMPANIES.map(company =>
      unescapeQuotesInObject(this.mapRawCompanyData(company)),
    );

  updateUser = newUser => {
    const { users } = this.state;
    const userIndex = users.findIndex(user => user.id === newUser.id);
    const newUsers = users.slice(0);
    newUsers[userIndex] = newUser;
    this.setState({ users: newUsers });
  };

  updateCompany = newCompany => {
    const { companies } = this.state;
    const companyIndex = companies.findIndex(
      company => company.id === newCompany.id,
    );
    const newCompanies = companies.slice(0);
    newCompanies[companyIndex] = newCompany;
    this.setState({ companies: newCompanies });
  };

  saveUser = async user => {
    const imageUploadError = await this.uploadImage(user);
    if (imageUploadError) return imageUploadError;

    const requestBody = {
      FIRST_NAME: user.name,
      COMPANY_NAME: user.company,
      EMAIL: user.email,
      JOB_TITLE: user.companyPosition,
      LAST_NAME: user.lastName,
      MIDDLE_NAME: user.patronymic,
      PASSPORT_AUTHORITY: user.passportPlaceCreated,
      PASSPORT_ISSUE_DATE: user.passportCreated,
      BIRTH_DATE: user.birthday,
      PASSPORT_NUMBER: user.passport,
      REGISTRATION_ADDRESS: user.addressOfRegistration,
      SPHERE_OF_ACTIVITY: user.expertise,
      TELEPHONE: user.phone,
      SNILS: user.snils,
      DIPLOMA_REG_NUMBER: user.diplomaRegNumber,
      DIPLOMA_QUALIFICATION: user.diplomaQualification,
      DIPLOMA_SERIES: user.diplomaSeries,
      DIPLOMA_NUMBER: user.diplomaNumber,
      MKT_SUBSCRIPTION: user.subscribe,
      CITIZENSHIP: user.citizenship,
      CITIZENSHIP_CODE: user.citizenshipCode,
    };

    const options = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    };

    try {
      const response = await fetch(`${routes.user.edit}${user.id}`, options);
      const json = await response.json();

      if (json.status === 'success') {
        return null;
      } else if(json.status === 'error'){
        return json.errors[0]?.message || messages.unknownError;
      }else{
        throw new Error(json.errors[0]);
      }
    } catch (e) {
      const errorText = `${e.name} : ${e.message}`;
        const title = document.getElementsByTagName('h1')[0]?.innerHTML || '';
        const currentLocation = window.location.pathname;
        const error = {
          errors: errorText,
          page: `${title}. URL: ${currentLocation}`
        };
      openErrorMsgForm(error);
      return messages.unknownError;
    }
  };

  uploadImage = async user => {
    if (!user?.personalPhotoFile) return null;

    const options = {
      method: 'POST',
      credentials: 'include',
    };

    const formData = new FormData();
    formData.append('file', user.personalPhotoFile);
    options.body = formData;

    try {
      const response = await fetch(routes.user.avatar, options);
      const json = await response.json();
      if (json.status === 'success') {
        const newUser = { ...user };
        newUser.personalPhotoFile = null;
        this.updateUser(newUser);
        return null;
      } else {
        return json.errors[0].message;
      }
    } catch (e) {
      return messages.unknownError;
    }
  };

  clearAvatar = async user => {
    const options = {
      method: 'DELETE',
      credentials: 'include',
    };

    try {
      const response = await fetch(routes.user.avatar, options);
      const json = await response.json();

      if (json.status === 'success') {
        const newUser = { ...user };
        newUser.personalPhoto = null;
        newUser.personalPhotoFile = null;
        this.updateUser(newUser);
        return null;
      } else {
        return json.errors[0].message;
      }
    } catch (e) {
      return messages.unknownError;
    }
  };

  saveCompany = async company => {
    const requestBody = {
      BANK: company.bankName,
      BANK_ACCOUNT: company.paymentAccount,
      CORR_ACCOUNT: company.paymentCorAccount,
      BIK: company.bankBik,
      CITY: company.city,
      CITY_CODE: company.cityCode,
      COMPANY_FULLNAME: company.companyName,
      COMPANY_NAME: company.companyName,
      COMPANY_ID: company.id,
      INN: company.inn,
      KPP: company.kpp,
      LEGAL_ADDRESS: company.address,
      LOCATION_ADDRESS: company.factAddress,
    };

    const options = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    };

    try {
      const response = await fetch(
        `${routes.user.updateCompany}${company.id}`,
        options,
      );
      const json = await response.json();
      if (json.status === 'success') {
        return null;
      } else if (json.status === 'error'){
        return json.errors[0]?.message || messages.unknownError;
      }else{
        throw new Error(json.errors[0]);
      }
    } catch (e) {
      const errorText = `${e.name} : ${e.message}`;
        const title = document.getElementsByTagName('h1')[0]?.innerHTML || '';
        const currentLocation = window.location.pathname;
        const error = {
          errors: errorText,
          page: `${title}. URL: ${currentLocation}`
        };
      openErrorMsgForm(error);
      return messages.unknownError;
    }
  };

  render() {
    const { users, companies } = this.state;

    return (
      <React.Fragment>
        <ProfileContext.Provider
          value={{
            updateUser: this.updateUser,
            saveUser: this.saveUser,
            updateCompany: this.updateCompany,
            saveCompany: this.saveCompany,
            clearAvatar: this.clearAvatar,
          }}
        >
          <Users users={users} />
          <Companies companies={companies} />
        </ProfileContext.Provider>
      </React.Fragment>
    );
  }
}

export default ProfilesTab;
